<template>
  <div>
    <!-- Header with Filters -->
    <v-card class="mb-4">
      <v-card-title>
        <v-icon left>mdi-engine</v-icon>
        DG Billing History
        <v-spacer />
        <v-btn
          color="primary"
          @click="refreshData"
          :loading="loading"
        >
          <v-icon left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Filters -->
        <v-row>
          <v-col cols="12" md="3">
            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedStartDate"
                  label="Start Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  clearable
                  @click:clear="filters.startDate = null"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="filters.startDate"
                @input="startDateMenu = false"
              />
            </v-menu>
          </v-col>

          <v-col cols="12" md="3">
            <v-menu
              v-model="endDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedEndDate"
                  label="End Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  clearable
                  @click:clear="filters.endDate = null"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="filters.endDate"
                @input="endDateMenu = false"
              />
            </v-menu>
          </v-col>

          <v-col cols="12" md="2">
            <v-text-field
              v-model="filters.minEfficiency"
              label="Min kWh/L"
              type="number"
              prepend-icon="mdi-gauge"
              clearable
              step="0.1"
            />
          </v-col>

          <v-col cols="12" md="2">
            <v-text-field
              v-model="filters.maxEfficiency"
              label="Max kWh/L"
              type="number"
              prepend-icon="mdi-gauge"
              clearable
              step="0.1"
            />
          </v-col>

          <v-col cols="12" md="2">
            <v-text-field
              v-model="filters.minDieselConsumption"
              label="Min Diesel (L)"
              type="number"
              prepend-icon="mdi-gas-station"
              clearable
            />
          </v-col>
        </v-row>

        <!-- Search Bar -->
        <v-row>
          <v-col cols="12" md="8">
            <v-text-field
              v-model="searchText"
              label="Search DG records..."
              prepend-icon="mdi-magnify"
              clearable
              outlined
              dense
              placeholder="Search by ID, diesel consumption, running hours, or creator"
              @input="performSearch"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-btn
              color="success"
              @click="exportAllRecords"
              :disabled="filteredDGBillingRecords.length === 0"
            >
              <v-icon left>mdi-download</v-icon>
              Export All
            </v-btn>
            <v-btn
              color="info"
              class="ml-2"
              @click="exportSummary"
              :disabled="filteredDGBillingRecords.length === 0"
            >
              <v-icon left>mdi-chart-line</v-icon>
              Summary
            </v-btn>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" class="text-right">
            <v-btn
              color="grey"
              text
              @click="clearFilters"
            >
              Clear Filters
            </v-btn>
            <v-btn
              color="primary"
              class="ml-2"
              @click="applyFilters"
            >
              Apply Filters
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="filteredDGBillingRecords"
      :loading="loading"
      :items-per-page="itemsPerPage"
      :footer-props="{
        itemsPerPageOptions: [10, 25, 50, 100]
      }"
      class="elevation-1"
      sort-by="createdAt"
      sort-desc
    >
      <!-- Billing Period Column -->
      <template #item.billingPeriod="{ item }">
        <div>
          <div class="text-subtitle-2">{{ formatBillingPeriod(item.billingPeriod) }}</div>
          <div class="text-caption grey--text">
            {{ getBillingDuration(item.billingPeriod) }} days
          </div>
        </div>
      </template>

      <!-- DG Consumption Column -->
      <template #item.dgConsumption="{ item }">
        <div class="text-right">
          <div class="text-subtitle-2">
            {{ formatNumber(item.dgConsumptionData.totalConsumption) }}
          </div>
          <div class="text-caption grey--text">kWh</div>
        </div>
      </template>

      <!-- Diesel Consumption Column -->
      <template #item.dieselConsumption="{ item }">
        <div class="text-right">
          <div class="text-subtitle-2">{{ formatNumber(item.dieselConsumption) }}</div>
          <div class="text-caption grey--text">Litres</div>
        </div>
      </template>

      <!-- Running Hours Column -->
      <template #item.runningHours="{ item }">
        <div class="text-right">
          <div class="text-subtitle-2">{{ formatNumber(item.runningHours) }}</div>
          <div class="text-caption grey--text">Hours</div>
        </div>
      </template>

      <!-- kWh per Litre Efficiency Column -->
      <template #item.kwhPerLitre="{ item }">
        <div class="text-right">
          <v-chip
            small
            :color="getEfficiencyColor(item.efficiencyMetrics.kwhPerLitre)"
            text-color="white"
          >
            {{ formatEfficiency(item.efficiencyMetrics.kwhPerLitre) }}
          </v-chip>
          <div class="text-caption grey--text">kWh/L</div>
        </div>
      </template>

      <!-- Litres per Hour Column -->
      <template #item.litresPerHour="{ item }">
        <div class="text-right">
          <div class="text-subtitle-2">
            {{ formatEfficiency(item.efficiencyMetrics.litresPerHour) }}
          </div>
          <div class="text-caption grey--text">L/hr</div>
        </div>
      </template>

      <!-- Created Date Column -->
      <template #item.createdAt="{ item }">
        <div>
          <div class="text-subtitle-2">{{ formatDate(item.createdAt) }}</div>
          <div class="text-caption grey--text">{{ formatTime(item.createdAt) }}</div>
        </div>
      </template>

      <!-- Actions Column -->
      <template #item.actions="{ item }">
        <div class="table-action-group">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                color="primary"
                class="table-action-btn"
                aria-label="View"
                v-bind="attrs"
                v-on="on"
                @click="viewDGRecord(item)"
              >
                <v-icon small>mdi-eye</v-icon>
              </v-btn>
            </template>
            <span>View</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                color="warning"
                class="table-action-btn"
                aria-label="Edit"
                v-bind="attrs"
                v-on="on"
                @click="editDGRecord(item)"
              >
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </template>
            <span>Edit</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                color="info"
                class="table-action-btn"
                aria-label="Export"
                v-bind="attrs"
                v-on="on"
                @click="exportDGRecord(item)"
              >
                <v-icon small>mdi-download</v-icon>
              </v-btn>
            </template>
            <span>Export</span>
          </v-tooltip>
        </div>
      </template>

      <!-- No Data -->
      <template #no-data>
        <div class="text-center pa-4">
          <v-icon size="64" color="grey">mdi-engine-off</v-icon>
          <div class="text-h6 grey--text mt-2">No DG billing records found</div>
          <div class="text-body-2 grey--text">
            {{ hasActiveFilters
              ? 'Try adjusting your filters'
              : 'Create your first DG billing record' }}
          </div>
          <v-btn
            v-if="!hasActiveFilters"
            color="primary"
            class="mt-4"
            @click="$router.push('/dg-billing-entry')"
          >
            <v-icon left>mdi-plus</v-icon>
            Create New DG Bill
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <!-- Summary Statistics -->
    <v-card class="mt-4" v-if="filteredDGBillingRecords.length > 0">
      <v-card-title class="subtitle-1">
        <v-icon left>mdi-chart-box</v-icon>
        DG Performance Summary
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="2">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h4 primary--text">{{ filteredDGBillingRecords.length }}</div>
                <div class="text-subtitle-2">Total Records</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="2">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h4 success--text">{{ formatNumber(totalDGConsumption) }}</div>
                <div class="text-subtitle-2">Total kWh</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="2">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h4 warning--text">{{ formatNumber(totalDieselConsumption) }}</div>
                <div class="text-subtitle-2">Total Diesel (L)</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="2">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h4 info--text">{{ formatNumber(totalRunningHours) }}</div>
                <div class="text-subtitle-2">Total Hours</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="2">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h4 secondary--text">
                  {{ formatEfficiency(averageKwhPerLitre) }}
                </div>
                <div class="text-subtitle-2">Avg kWh/L</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="2">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h4 error--text">{{ formatEfficiency(averageLitresPerHour) }}</div>
                <div class="text-subtitle-2">Avg L/hr</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { DGExportService, DGSearchService } from '../../services/dgExportService';

export default {
  name: 'DGBillingList',

  data() {
    return {
      itemsPerPage: 25,
      startDateMenu: false,
      endDateMenu: false,
      searchText: '',
      searchResults: [],
      filters: {
        startDate: null,
        endDate: null,
        minEfficiency: null,
        maxEfficiency: null,
        minDieselConsumption: null,
      },
      appliedFilters: {
        startDate: null,
        endDate: null,
        minEfficiency: null,
        maxEfficiency: null,
        minDieselConsumption: null,
      },
      headers: [
        { text: 'Billing Period', value: 'billingPeriod', sortable: false },
        {
          text: 'DG Consumption', value: 'dgConsumption', sortable: true, align: 'right',
        },
        {
          text: 'Diesel (L)', value: 'dieselConsumption', sortable: true, align: 'right',
        },
        {
          text: 'Running Hours', value: 'runningHours', sortable: true, align: 'right',
        },
        {
          text: 'kWh/L', value: 'kwhPerLitre', sortable: true, align: 'right',
        },
        {
          text: 'L/hr', value: 'litresPerHour', sortable: true, align: 'right',
        },
        { text: 'Created', value: 'createdAt', sortable: true },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
          align: 'center',
          width: '132px',
        },
      ],
    };
  },

  computed: {
    ...mapGetters('dgBilling', ['allDGBillingRecords', 'loading']),

    dgBillingRecords() {
      return this.allDGBillingRecords || [];
    },

    filteredDGBillingRecords() {
      // Start with search results if search is active, otherwise use all records
      let filtered = this.searchText ? this.searchResults : [...this.dgBillingRecords];

      // Apply additional filters
      const searchCriteria = {
        ...this.appliedFilters,
        searchText: this.searchText,
      };

      // Use DGSearchService for consistent filtering
      filtered = DGSearchService.searchDGRecords(filtered, searchCriteria);

      return filtered;
    },

    formattedStartDate() {
      return this.filters.startDate ? this.formatDate(this.filters.startDate) : '';
    },

    formattedEndDate() {
      return this.filters.endDate ? this.formatDate(this.filters.endDate) : '';
    },

    hasActiveFilters() {
      return Object.values(this.appliedFilters).some((value) => value !== null && value !== '');
    },

    // Summary statistics
    totalDGConsumption() {
      return this.filteredDGBillingRecords.reduce(
        (sum, record) => sum + (record.dgConsumptionData?.totalConsumption || 0),
        0,
      );
    },

    totalDieselConsumption() {
      return this.filteredDGBillingRecords.reduce(
        (sum, record) => sum + (record.dieselConsumption || 0),
        0,
      );
    },

    totalRunningHours() {
      return this.filteredDGBillingRecords.reduce(
        (sum, record) => sum + (record.runningHours || 0),
        0,
      );
    },

    averageKwhPerLitre() {
      if (this.filteredDGBillingRecords.length === 0) return 0;
      const total = this.filteredDGBillingRecords.reduce(
        (sum, record) => sum + (record.efficiencyMetrics?.kwhPerLitre || 0),
        0,
      );
      return total / this.filteredDGBillingRecords.length;
    },

    averageLitresPerHour() {
      if (this.filteredDGBillingRecords.length === 0) return 0;
      const total = this.filteredDGBillingRecords.reduce(
        (sum, record) => sum + (record.efficiencyMetrics?.litresPerHour || 0),
        0,
      );
      return total / this.filteredDGBillingRecords.length;
    },
  },

  async created() {
    await this.refreshData();
  },

  methods: {
    ...mapActions('dgBilling', ['fetchDGBillingRecords']),

    async refreshData() {
      try {
        await this.fetchDGBillingRecords();

        // Show success message only if there were previous errors
        if (this.error) {
          this.$emit('show-message', {
            type: 'success',
            message: 'DG billing records refreshed successfully',
          });
        }
      } catch (error) {
        console.error('Error fetching DG billing records:', error);

        // Provide user-friendly error messages
        let errorMessage = 'Failed to load DG billing records';

        if (error.message) {
          if (error.message.includes('network') || error.message.includes('connection')) {
            errorMessage = 'Network connection failed. Please check your internet connection.';
          } else if (error.message.includes('timeout')) {
            errorMessage = 'Request timed out. Please try again.';
          } else if (error.message.includes('permission') || error.message.includes('403')) {
            errorMessage = 'Access denied. Please contact your administrator.';
          } else if (error.message.length < 100) {
            errorMessage = error.message;
          }
        }

        this.$emit('show-message', {
          type: 'error',
          message: errorMessage,
        });
      }
    },

    applyFilters() {
      this.appliedFilters = { ...this.filters };
    },

    clearFilters() {
      this.filters = {
        startDate: null,
        endDate: null,
        minEfficiency: null,
        maxEfficiency: null,
        minDieselConsumption: null,
      };
      this.appliedFilters = { ...this.filters };
      this.searchText = '';
      this.searchResults = [];
    },

    viewDGRecord(record) {
      const recordId = record._id || record.id;
      this.$router.push(`/dg-billing-history/${recordId}`);
    },

    editDGRecord(record) {
      const recordId = record._id || record.id;
      this.$router.push(`/dg-billing-history/${recordId}/edit`);
    },

    exportDGRecord(record) {
      try {
        if (!record) {
          throw new Error('No record selected for export');
        }

        DGExportService.exportDGRecordToCSV(record);
        this.$emit('show-message', {
          type: 'success',
          message: 'DG Record exported successfully',
        });
      } catch (error) {
        console.error('Error exporting DG record:', error);

        let errorMessage = 'Failed to export DG record';
        if (error.message && error.message.length < 100) {
          errorMessage = error.message;
        }

        this.$emit('show-message', {
          type: 'error',
          message: errorMessage,
        });
      }
    },

    exportAllRecords() {
      try {
        const recordsToExport = this.filteredDGBillingRecords;
        if (recordsToExport.length === 0) {
          this.$emit('show-message', {
            type: 'warning',
            message: 'No records available to export. Try adjusting your filters or create some DG billing records first.',
          });
          return;
        }

        // Validate records before export
        const validRecords = recordsToExport.filter((record) => (
          record && record.billingPeriod && record.dieselConsumption !== undefined
        ));

        if (validRecords.length === 0) {
          throw new Error('No valid records found for export');
        }

        if (validRecords.length < recordsToExport.length) {
          console.warn(`${recordsToExport.length - validRecords.length} invalid records skipped during export`);
        }

        const filename = `dg_billing_records_${new Date().toISOString().split('T')[0]}.csv`;
        DGExportService.exportDGRecordsToCSV(validRecords, filename);

        this.$emit('show-message', {
          type: 'success',
          message: `${validRecords.length} DG records exported successfully`,
        });
      } catch (error) {
        console.error('Error exporting DG records:', error);

        let errorMessage = 'Failed to export DG records';
        if (error.message && error.message.length < 100) {
          errorMessage = error.message;
        }

        this.$emit('show-message', {
          type: 'error',
          message: errorMessage,
        });
      }
    },

    exportSummary() {
      try {
        const recordsToExport = this.filteredDGBillingRecords;
        if (recordsToExport.length === 0) {
          this.$emit('show-message', {
            type: 'warning',
            message: 'No records available to export summary. Create some DG billing records first.',
          });
          return;
        }

        // Validate records have required data for summary
        const validRecords = recordsToExport.filter((record) => record
          && record.dieselConsumption !== undefined
          && record.runningHours !== undefined
          && record.dgConsumptionData);

        if (validRecords.length === 0) {
          throw new Error('No records with complete data found for summary export');
        }

        const filename = `dg_billing_summary_${new Date().toISOString().split('T')[0]}.csv`;
        DGExportService.exportDGSummaryToCSV(validRecords, filename);

        this.$emit('show-message', {
          type: 'success',
          message: `DG Summary exported successfully (${validRecords.length} records included)`,
        });
      } catch (error) {
        console.error('Error exporting DG summary:', error);

        let errorMessage = 'Failed to export DG summary';
        if (error.message && error.message.length < 100) {
          errorMessage = error.message;
        }

        this.$emit('show-message', {
          type: 'error',
          message: errorMessage,
        });
      }
    },

    performSearch() {
      if (!this.searchText || this.searchText.trim() === '') {
        this.searchResults = [];
        return;
      }

      // Perform search using DGSearchService
      const searchCriteria = {
        searchText: this.searchText.trim(),
      };

      this.searchResults = DGSearchService.searchDGRecords(this.dgBillingRecords, searchCriteria);
    },

    // Formatting methods
    formatBillingPeriod(period) {
      const start = new Date(period.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const end = new Date(period.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `${start} - ${end}`;
    },

    getBillingDuration(period) {
      const start = new Date(period.startDate);
      const end = new Date(period.endDate);
      const diffTime = Math.abs(end - start);
      return Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
    },

    formatNumber(value) {
      return value ? value.toLocaleString() : '0';
    },

    formatEfficiency(value) {
      return value ? value.toFixed(2) : '0.00';
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString();
    },

    formatTime(date) {
      return new Date(date).toLocaleTimeString();
    },

    // Efficiency color coding
    getEfficiencyColor(kwhPerLitre) {
      if (kwhPerLitre >= 3.5) return 'success';
      if (kwhPerLitre >= 2.5) return 'warning';
      return 'error';
    },
  },
};
</script>

<style scoped>
.table-action-group {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 112px;
  white-space: nowrap;
}

.table-action-btn {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
